<template>
  <div class="s-datepicker"
       :class="$attrs.class"
       :style="$attrs.style"
  >
    <Field
      :name="validationFieldName"
      :label="validationFieldLabel || validationFieldName"
      :rules="validationRules"
      :model-value="models.localDate"
      @update:model-value="(newValue) => (models.localDate = newValue)"
      v-slot="slotProps"
    >
      <template v-if="!hideControl">
        <span
          v-if="label"
          :id="`label-${datepickerId}`"
          class="s-datepicker__label mb-2 f-label d-inline-block"
          data-cy="datepicker-label"
        >{{ label }}</span>
        <!--eslint-disable-next-line sdui/require-focus-restore-on-disabled-button-->
        <b-button
          class="s-datepicker__button p-2 w-100 text-left form-control form-control-lg d-flex align-items-center"
          :class="{ 's-datepicker__button--invalid': isInvalid }"
          variant="transparent"
          :disabled="disabled"
          :aria-invalid="isInvalid ? 'true' : undefined"
          :aria-describedby="descriptionId"
          data-cy="datepicker-button"
          @click="showModal = true"
        >
          {{ $dateFormat.date_yearlong(selectedDate) }}
        </b-button>
        <!-- invalid text -->
        <span
          v-if="isInvalid"
          :id="`invalid-${datepickerId}`"
          class="s-datepicker__invalid text-danger-dark nano mt-1 mb-0"
          data-cy="datepicker-invalid-text"
        >
          {{ invalidFieldMessage }}
        </span>
      </template>

      <!-- datepicker modal -->
      <SModal
        v-if="!disabled"
        v-model="displayModal"
        :dialog-class="hasMobileSize ? '' : 's-datepicker__modal'"
        :modal-class="isMobile ? '' : 'modal-fullscreen-sm-down'"
        :show-footer-gradient="false"
        centered
        footer-bg-variant="secondary"
        :hide-header="!hasMobileSize"
        :header-title="$t('ui.date_time_picker.date_select')"
        data-cy="s-datepicker-modal"
        @hide="closeModal"
      >
        <SDatePickerRaw
          v-if="displayModal"
          v-bind="filteredAttrs"
          :model-value="slotProps.componentField.modelValue"
          :value="slotProps.componentField.modelValue"
          :min-date="minMaxDatesFormatted.minDate"
          :max-date="minMaxDatesFormatted.maxDate"
          :extracalendar-attrs="calendarAttrs"
          :custom-class="calendarClass"
          :size="size"
          @update:model-value="slotProps.handleChange"
          @page-changed="handlePageChange"
          @month-popover-triggered="preventModalCloseOnEsc"
        />

        <slot />

        <template #modal-footer>
          <b-button variant="primary"
                    class="w-100 form-control form-control-lg"
                    data-cy="confirm-date-btn"
                    @click="closeModal"
          >
            {{ $t('ui.date_time_picker.confirm') }}
          </b-button>
        </template>
      </SModal>
    </Field>
  </div>
</template>

<script>
import { defineAsyncComponent, onMounted, ref, watch } from 'vue'
import isMobileDeviceMixing from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import dateTimePicker from 'src/mixins/miscellaneous/dateTimePicker.js'
import { Field, useFieldError } from 'vee-validate'

const SDatePickerRaw = defineAsyncComponent({
  loader: () => import('src/components/base/datepicker/SDatePickerRaw.vue'),
  delay: 50,
  timeout: 3000,
})

export default {
  // TODO VUE3: This is a mix of Options API and Composition API which should be removed by refactoring
  // the whole component to Composition API. Right now it's needed to access validation state of VeeValidate.
  setup(props) {
    const validationErrorMessage = ref(undefined)

    onMounted(() => {
      const hasFieldError = useFieldError(props.validationFieldName)

      watch(hasFieldError, (newVal) => {
        validationErrorMessage.value = newVal
      })
    })

    return {
      validationErrorMessage,
    }
  },

  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  name: 'SDatePicker',
  components: { SDatePickerRaw, Field },
  mixins: [isMobileDeviceMixing, dateTimePicker],
  props: {
    label: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    hideControl: {
      type: Boolean,
      default: false,
    },

    toggleModal: {
      type: Boolean,
      default: false,
    },

    // Not required to use for field validation. Only use this for fields that are not part of a form and validate
    // special use cases.
    invalidText: {
      type: String,
      default: '',
    },

    /*
    Value should match a string in "validations.attributes" (see [LANG].json)
    Setting this prop will also render attribute name="validationFieldName" on the input element.
    Must be unique within the <VForm> tag!
     */
    validationFieldName: {
      type: String,
      default: 'date',
    },

    /*
    Rules to validate, example:
      - validation-rules="required|email"
    See all available rules in src/config/setup/plugins/veeValidate/index.js
    */
    validationRules: {
      type: [String, Object],
      default: 'required',
    },

    /*
    validationFieldName must be unique within the <VForm> tag. If that is not possible or if you need to translate
    a different attribute name, you can set this prop to a different value that exists in "validations.attributes".
     */
    validationFieldLabel: {
      type: String,
      default: '',
    },

    // datepicker props --------------------------
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium'].includes(value),
    },

    modelValue: {
      type: [String, Date],
      default: '',
    },

    minDate: {
      type: String,
      default: null,
    },

    maxDate: {
      type: String,
      default: null,
    },

    // When true, forces out-of-date ranges to be snapped to the min and max dates. When false, display date passed to it as-is.
    isRangeEnforced: {
      type: Boolean,
      default: true,
    },

    calendarClass: {
      type: String,
      default: '',
    },

    calendarAttrs: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      datepickerId: null,
      selectedDate: null,
      showModal: false,
      modalShouldClose: true,
      localDate: '',
      models: {
        localDate: '',
      },
    }
  },

  computed: {
    filteredAttrs() {
      const { class: _cls, style: _style, name: _name, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    displayModal: {
      get() {
        return this.showModal || this.toggleModal
      },

      set(value) {
        this.showModal = value
      },
    },

    minMaxDatesFormatted() {
      return {
        minDate: this.minDate && this.$moment(this.minDate).toDate(),
        maxDate: this.maxDate && this.$moment(this.maxDate).toDate(),
      }
    },

    invalidFieldMessage() {
      return this.invalidText || this.validationErrorMessage
    },

    isInvalid() {
      return !!this.invalidText || !!this.validationErrorMessage
    },

    descriptionId() {
      // is invalid and has label
      if (this.isInvalid && this.label) return `invalid-${this.datepickerId} label-${this.datepickerId}`

      // is invalid, but no label
      if (this.isInvalid) return `invalid-${this.datepickerId}`

      // is valid and has label
      if (this.label) return `label-${this.datepickerId}`

      // is valid and no label
      return undefined
    },
  },

  emits: ['update:modelValue', 'close', 'page-changed'],

  methods: {
    getCurrentSelectedDate() {
      return this.mergeDateTime(this.models.localDate, this.localDate, 'date')
    },

    onSelect() {
      this.selectedDate = this.getCurrentSelectedDate()
      this.$emit('update:modelValue', this.selectedDate)
    },

    closeModal(event) {
      // prevent modal from closing comepletly if month popover is open (e.g. when user pressed ESC to only close the month popover)
      if (!this.modalShouldClose && event) return event.preventDefault()

      this.showModal = false
      this.$emit('close')
    },

    preventModalCloseOnEsc(monthPopoverIsOpen) {
      this.modalShouldClose = !monthPopoverIsOpen
    },

    handlePageChange(event) {
      if (!event || !event.length) return
      this.$emit('page-changed', event[0])
    },
  },

  watch: {
    modelValue: {
      handler(newValue, oldValue) {
        const momentVal = this.$moment(newValue).isValid() ? this.$moment(newValue) : this.$moment().add(1, 'days')

        // Force dates into the min and max range if out-of-range dates are passed, unless flag overrides it
        const minMaxFixedDate =
          !this.isRangeEnforced ||
          ((!this.minDate || momentVal.isSameOrAfter(this.minDate)) && (!this.maxDate || momentVal.isSameOrBefore(this.maxDate)))
            ? momentVal
            : this.$moment(this.minDate || this.maxDate)

        this.localDate = minMaxFixedDate
        this.models.localDate = minMaxFixedDate.toDate()

        // emit the new date and prevent endless loop
        if (oldValue && minMaxFixedDate.isSame(oldValue)) return
        this.onSelect()
      },

      immediate: true,
    },

    'models.localDate': {
      handler() {
        this.onSelect()
      },
    },
  },

  created() {
    this.datepickerId = this.$uuid()
  },
}
</script>

<style lang="scss" scoped>
.s-datepicker__button {
  background-color: $color-secondary;
  border: 1px solid $color-gray-100;
}

.s-datepicker__button--invalid {
  border-color: $color-danger;
  background-color: $color-danger-light;
}
</style>

<!-- This is unscoped because the modal is attached to the body -->
<style lang="scss">
// set custom width for date picker modals
.s-datepicker__modal {
  width: 356px !important;
}
</style>
